import { useEffect } from "react";
import { useLocation } from "react-router";
import { INDEXABLE, absoluteUrl } from "../config/site";
import { PAGE_META, type PageKey } from "../content/meta";

/**
 * Per-route document head: title, description, canonical, robots, og:*.
 *
 * A HOOK rather than a `<PageMeta>` element, because several routes return
 * early — AgeGate has a declined branch, Login redirects when you are already
 * signed in, AuthCallback has three terminal states. An element placed in the
 * happy-path JSX simply doesn't render on those branches, so the page keeps
 * whatever title the PREVIOUS route set. A hook at the top of the component
 * runs on every branch, which is the property that actually matters here.
 *
 * There is no Helmet-style library. The whole job is five mutations against
 * `document.head`, and what a library would buy — a declarative tree
 * reconciled across nested components — is a liability when the rule is
 * "exactly one canonical, exactly one title". Imperative code makes the
 * single-owner rule the obvious reading.
 *
 * ONE call per route, at the top of the route component.
 *
 * ── The canonical is built from the ROUTE, not from window.location ──────────
 * `location.search` is dropped. Every query string this app uses carries UI
 * state rather than naming a distinct document: `/pricing?model=nba-props` is
 * the pricing page with a tile preselected, `/app?unavailable=x` is the
 * directory with a notice on it. Canonicalising those to themselves asks a
 * crawler to index one page once per model id. `location.hash` goes for the
 * same reason — `/why-kev#the-idea` is the marketing page.
 *
 * The HOST comes from the build (config/site.ts), never from the browser. That
 * is what makes a request arriving on the *.cloudfront.net name canonicalise
 * to the real domain instead of competing with it in the index.
 */
export function usePageMeta(page: PageKey): void {
  const { pathname } = useLocation();
  const meta = PAGE_META[page];
  const noindex = !INDEXABLE || "robots" in meta;

  useEffect(() => {
    document.title = meta.title;
    setMetaTag("name", "description", meta.description);
    setMetaTag("property", "og:title", meta.title);
    setMetaTag("property", "og:description", meta.description);
    setMetaTag("property", "og:url", absoluteUrl(pathname));

    /* Trailing slashes normalise away (except at the root), so /pricing and
       /pricing/ — react-router serves both — name one canonical document. */
    const canonicalPath = pathname !== "/" ? pathname.replace(/\/+$/, "") || "/" : "/";
    setLinkTag("canonical", absoluteUrl(canonicalPath));

    /*
     * The robots tag is always WRITTEN, never merely omitted on the index-me
     * path. index.html ships a static `noindex, nofollow` so the pre-hydration
     * document is safe by default; if this only ever added noindex, that static
     * tag would survive on every page of an indexable build and silently
     * de-index the entire site — a failure that looks like nothing at all until
     * someone notices the site has no search traffic.
     */
    setMetaTag(
      "name",
      "robots",
      noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    );
  }, [meta.title, meta.description, noindex, pathname]);
}

function setMetaTag(keyAttr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${keyAttr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(keyAttr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkTag(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
